// AI personality archetypes. Each tunes the same scoring pipeline
// differently — threshold for "is this attack good enough", how much
// to favor the leader, how much to value region bonuses, a flat EV
// bias, and softmax temperature for top-K sampling. Three archetypes
// is enough for v1 (we have 2-3 AI seats per game) and creates
// noticeably different play styles without an explosion of knobs.
//
// Numbers are starting points — playtest, then tune.

export type PersonalityId = 'aggressive' | 'cautious' | 'wildcard';

export interface AiPersonality {
  readonly id: PersonalityId;

  /**
   * Skip attacks whose adjusted EV falls below this threshold. Lower =
   * more willing to gamble. Cautious sits at +0.05 (only takes clearly
   * favorable fights); Aggressive at -0.10 (takes coin-flips).
   */
  readonly attackEvThreshold: number;

  /**
   * 0..1. Multiplier on the leader-targeting bonus. 0 = ignores leader
   * size; 1 = full sensitivity. Higher = more anti-kingmaker behavior.
   * Floored at 1.0 so wounded opponents are never DEPRIORITIZED.
   */
  readonly threatWeight: number;

  /**
   * 0..1. Scales the region-completion / denial EV bonus. 0 = ignores
   * regions; 1 = a +3 region completion adds +3 EV to the attack.
   */
  readonly regionWeight: number;

  /**
   * Flat additive shift on every attack's adjusted EV. Pushes
   * borderline attacks through the threshold (Aggressive) or stays at
   * 0 to keep scoring honest (Cautious).
   */
  readonly aggressionBias: number;

  /**
   * 0..1. Softmax temperature for top-K sampling. 0 ≈ deterministic
   * (always pick the best); higher = more spontaneous variance among
   * the top candidates.
   */
  readonly randomness: number;
}

export const AGGRESSIVE: AiPersonality = {
  id: 'aggressive',
  attackEvThreshold: -0.1,
  threatWeight: 0.5,
  regionWeight: 0.7,
  aggressionBias: 0.1,
  randomness: 0.2,
};

export const CAUTIOUS: AiPersonality = {
  id: 'cautious',
  attackEvThreshold: 0.05,
  threatWeight: 0.9,
  regionWeight: 1.0,
  aggressionBias: 0,
  randomness: 0.05,
};

export const WILDCARD: AiPersonality = {
  id: 'wildcard',
  attackEvThreshold: -0.05,
  threatWeight: 0.6,
  regionWeight: 0.6,
  aggressionBias: 0.05,
  randomness: 0.45,
};

/**
 * Round-robin order used by `assignPersonality`. Deterministic — seat
 * 0 → Aggressive, seat 1 → Cautious, seat 2 → Wildcard, then wraps.
 * Ordering chosen to give the first AI seat the most-aggressive style
 * by default, since it tends to act first and benefits from pressure.
 */
export const PERSONALITIES: readonly AiPersonality[] = [
  AGGRESSIVE,
  CAUTIOUS,
  WILDCARD,
];

/**
 * Fallback when an AI player record has no personality field. Cautious
 * is the closest match to v0.4 reference behavior — modest threshold,
 * near-zero randomness, full region awareness — so legacy or
 * test-built states without an explicit personality play as if the
 * upgrade hadn't happened.
 */
export const DEFAULT_PERSONALITY: AiPersonality = CAUTIOUS;

/**
 * Pick a personality for an AI seat by its position in `playerOrder`
 * among AI seats only (not raw seat index — humans don't consume a
 * slot). Deterministic. The lobby slice (v0.7) will let players
 * override this assignment.
 */
export function assignPersonality(aiSeatIndex: number): AiPersonality {
  return PERSONALITIES[aiSeatIndex % PERSONALITIES.length]!;
}
